import config from "lib/config";
import { createTheme } from "@mui/material/styles";
import { getCssVariableOrFallback } from "../utils";
import * as locales from "@mui/material/locale";

const app_locales = {
  "en-US": { locale: "English", tag: "en-US", name: "enUS" },
  "sv-SE": { locale: "Swedish", tag: "sv-SE", name: "svSE" },
  "fi-FI": { locale: "Finnish", tag: "fi-Fi", name: "fiFI" },
  "nb-NO": { locale: "Norwegian", tag: "nb-NO", name: "nbNO" },
};

const locale = config.culture || window?.__LUVITCONFIG?.culture || "en-US";
const app_locale = app_locales[locale]?.name || app_locales["en-US"].name;

const getPrimaryFont = () => {
  return getCssVariableOrFallback(
    "--primaryFont",
    '"Roboto", "Helvetica", "Arial", sans-serif',
  );
};

const getTextFont = () => {
  return getCssVariableOrFallback(
    "--primaryTextFont",
    '"Roboto", "Helvetica", "Arial", sans-serif',
  );
};

const getTextFontWeight = () => {
  const fontWeight = parseInt(
    getCssVariableOrFallback("--primaryTextFontWeight", "400"),
    10,
  );

  return Number.isNaN(fontWeight) ? 400 : fontWeight;
};

const themeSettings = {
  palette: {
    primary: {
      light: getCssVariableOrFallback("--primaryLightColor", "#EFF1FA"),
      main: getCssVariableOrFallback("--primaryColor", "#4A5BC9"),
    },
    secondary: {
      light: getCssVariableOrFallback("--colorSecondaryFocused", "#818594"),
      main: getCssVariableOrFallback("--colorSecondary", "#41475E"),
    },
    error: {
      light: getCssVariableOrFallback("--colorErrorLight", "#FBEAEA"),
      main: getCssVariableOrFallback("--colorErrorDark", "#BD2828"),
    },
    success: {
      light: getCssVariableOrFallback("--colorSuccessLight", "#EFF5EF"),
      main: getCssVariableOrFallback("--colorSuccessDark", "#2F7F33"),
    },
    warning: {
      light: getCssVariableOrFallback("--colorWarningLight", "#FDF2E8"),
      main: getCssVariableOrFallback("--colorWarningDark", "#B64D02"),
    },
    info: {
      light: getCssVariableOrFallback("--colorInfoLight", "#EAF4FB"),
      main: getCssVariableOrFallback("--colorInfoDark", "#0273B1"),
    },

    // Used by `getContrastText()` to maximize the contrast between
    // the background and the text.
    contrastThreshold: 3,
    // Used by the functions below to shift a color's luminance by approximately
    // two indexes within its tonal palette.
    // E.g., shift from Red 500 to Red 300 or Red 700.
    tonalOffset: 0.2,
  },
  typography: {
    fontFamily: getPrimaryFont(),
    h1: {
      fontFamily: getPrimaryFont(),
      fontWeight: 600,
    },
    h2: {
      fontFamily: getPrimaryFont(),
      fontWeight: 600,
      fontSize: "20px",
      lineHeight: "32px",
      letterSpacing: "0.15px",
    },
    h3: {
      fontFamily: getPrimaryFont(),
      fontWeight: 600,
      fontSize: "18px",
      lineHeight: "32px",
      letterSpacing: "0.15px",
    },
    h4: {
      fontFamily: getPrimaryFont(),
      fontWeight: 600,
      fontSize: "14px",
    },
    h5: {
      fontFamily: getPrimaryFont(),
    },
    h6: {
      fontFamily: getPrimaryFont(),
      fontWeight: 600,
    },
    subtitle1: {
      fontFamily: getTextFont(),
      fontWeight: getTextFontWeight(),
    },
    subtitle2: {
      fontFamily: getTextFont(),
      fontWeight: getTextFontWeight(),
    },
    body1: {
      fontFamily: getTextFont(),
      fontWeight: getTextFontWeight(),
    },
    body2: {
      fontFamily: getTextFont(),
      fontWeight: getTextFontWeight(),
    },
    button: {
      fontFamily: getTextFont(),
      fontWeight: getTextFontWeight(),
    },
    caption: {
      fontFamily: getTextFont(),
      fontWeight: getTextFontWeight(),
    },
    overline: {
      fontFamily: getTextFont(),
      fontWeight: getTextFontWeight(),
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "unset",
        },
        containedPrimary: {
          "&:hover": {
            color: getCssVariableOrFallback(
              "--primaryDarkContrastColor",
              "#FFF",
            ),
          },
        },
      },
    },
  },
};

const theme = createTheme(themeSettings, locales[app_locale]);

export default theme;
